import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, Drawer, Modal, Typography } from '@mui/material';
import CustomModal from '../../common/components/Modal/Modal';
import { httpService } from '../../utility/httpService';
import { httpConstants } from '../../constants';
import { API_BASE, API_GRAPH_BASE } from '../../config/env';
import { useGraphQLQuery } from '../../customHooks/useGraphQL';
import { GET_AI_SOLUTIONS_DATA } from '../../graphql/queries/commonQueries';
import ViewSolutionDetails from '../SimulationXPLR/ViewSolutionDetails';
import ShowToast, { ToastTypeEnum } from '../../common/components/ShowToast';
import EnhancedDataTable from '../../common/components/DataTable/EnhancedDataTable';
import Loader from '../../common/components/Loader';
import { refreshTaxonomy } from '../../slices/diagramViewerSlice';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store';

type AiSolutionRow = {
  id: string;
  serial: number;
  opportunity_name: string;
  impact?: string | null;
  roi?: string | null | React.ReactElement;
  complexity?: string | null | React.ReactElement;
  vault?: boolean;
  solution_enabler?: string | null;
};

interface AiSolutionsProps {
  diagramId: string;
  selectedCard?: any;
  refreshKey?: number;
  isLoading?: boolean;
}

const AiSolutions: React.FC<AiSolutionsProps> = ({
  diagramId,
  selectedCard,
  refreshKey,
  isLoading = false,
}) => {
  const currentOrg = localStorage.getItem('current_organization') || 'Empty';

  const [solutionData, setSolutionData] = useState<AiSolutionRow[]>([]);
  const [dataSourceType, setDataSourceType] = useState<'rest' | 'graphql' | null>(null);
  const [selectedSolutionFromLibrary, setSelectedSolutionFromLibrary] = useState<{
    id: string;
    name: string;
    vault?: boolean;
    solution_enabler?: string;
  }>({ id: '', name: '' });
  const [aiSolutionData, setAiSolutionData] = useState<any>(null);
  const [showVaultModal, setShowVaultModal] = useState<boolean>(false);
  const [enterpriseContextApplied, setEnterpriseContextApplied] = useState<boolean>(false);
  // const [isLoading, setIsLoading] = useState<boolean>(true);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [solutionToDelete, setSolutionToDelete] = useState<string>('');
  const [internalLoading, setInternalLoading] = useState<boolean>(true);
  const dispatch = useDispatch();
  const { diagramData } = useSelector((state: RootState) => state.diagramViewer);
  // Use combined loading state - either from parent prop or internal state
  const combinedLoading = isLoading || internalLoading;

  const { data, loading: qlLoading } = useGraphQLQuery(
    GET_AI_SOLUTIONS_DATA,
    { v1: { subprocessId: `${selectedCard?.id}` }, v2: ['name'] },
    { skip: !selectedCard?.id }
  );

  const fetchAISolution = useCallback(async (): Promise<any> => {
    try {
      const url = `${API_BASE}/api/v1/e2e_taxonomy/opportunities/?diagram_id=${diagramId}`;
      return await httpService<any>(httpConstants.METHOD_TYPE.GET, url);
    } catch (err) {
      return null;
    }
  }, [diagramId]);

  const fetchAISolutionByID = useCallback(
    async (solutionId: string): Promise<any> => {
      try {
        const url = `${API_BASE}/api/v1/e2e_taxonomy/opportunities/with-agent/${solutionId}/?diagram_id=${diagramId}&organization_id=${currentOrg}`;
        return await httpService<any>(httpConstants.METHOD_TYPE.GET, url);
      } catch (err) {
        return null;
      }
    },
    [diagramId, currentOrg]
  );

  const getSvgPath = (type: string) => {
    switch (type) {
      case 'HIGH':
        return '/images/GreenDot.svg';
      case 'MEDIUM':
        return '/images/YellowDot.svg';
      case 'LOW':
        return '/images/RedDot.svg';
      default:
        return '/images/GreenDot.svg';
    }
  };

  const patchSolution = async (data: any) => {
    // Transform the payload to match the required structure
    const payload = {
      roi: data.roi,
      impact_value: data.impact,
      complexity: data.complexity,
      roi_description: data.roi_description,
      complexity_description: data.complexity_description,
      description: data.description,
      description_llm: data.description_llm,
      llm_agent: data.llm_agent || [],
      solution_enabler: data.solution_enabler,
      benefits: data.benefits,
      data_sources: data.data_sources,
      key_requirements: data.key_requirements || [],
      diagram_id: data.diagram_id,
      embedded: data.agent?.embedded || [],
      ai_solution: data.agent?.ai_solution || [],
      organization_id: currentOrg,
      solution_id: selectedSolutionFromLibrary.id.toString(),
    };

    if (!payload.embedded || payload.embedded.length === 0) {
      delete payload.embedded;
    }

    const url = `${API_GRAPH_BASE}/custom-process/ai-opportunity?organization_id=${currentOrg}&solution_id=${selectedSolutionFromLibrary.id}`;
    const response = await httpService<any>(httpConstants.METHOD_TYPE.PATCH, url, payload);

    // Refresh the solutions list after successful update
    await fetchAISolution();

    return response;
  };

  const deleteSolution = async (id: string) => {
    try {
      const url = `${API_GRAPH_BASE}/custom-process/ai-opportunity?organization_id=${currentOrg}&solution_id=${id}`;
      const response = await httpService<any>(httpConstants.METHOD_TYPE.DELETE, url);

      setSolutionData((prevData) => prevData.filter((solution) => solution.id !== id));

      if (selectedSolutionFromLibrary.id === id) {
        setSelectedSolutionFromLibrary({ id: '', name: '' });
        setAiSolutionData(null);
      }

      setShowDeleteModal(false);

      dispatch(refreshTaxonomy(true));

      await fetchAISolution();

      ShowToast({ message: 'Solution deleted successfully', type: ToastTypeEnum.Success });

      return response;
    } catch (error) {
      console.error('Error deleting solution:', error);
      ShowToast({ message: 'Error deleting solution', type: ToastTypeEnum.Error });
      setShowDeleteModal(false);
    }
  };

  useEffect(() => {
    const loadSolutions = async () => {
      setInternalLoading(true);
      try {
        const response = await fetchAISolution();
        if (response?.results?.length > 0) {
          setSolutionData(
            response.results.map(
              (
                { id, opportunity_name, roi, complexity, impact, vault, solution_enabler }: any,
                index: number
              ) => ({
                id,
                serial: index + 1,
                opportunity_name,
                roi: roi ? (
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}
                  >
                    <img src={getSvgPath(roi)} alt={roi} style={{ width: 8, height: 8 }} />
                    <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>
                      {String(roi).charAt(0).toUpperCase() + String(roi).slice(1).toLowerCase()}
                    </Typography>
                  </Box>
                ) : (
                  '-'
                ),
                complexity: complexity ? (
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}
                  >
                    <img
                      src={getSvgPath(complexity)}
                      alt={complexity}
                      style={{ width: 8, height: 8 }}
                    />
                    <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>
                      {String(complexity).charAt(0).toUpperCase() +
                        String(complexity).slice(1).toLowerCase()}
                    </Typography>
                  </Box>
                ) : (
                  '-'
                ),
                impact: impact || '-',
                vault: vault || false,
                solution_enabler: solution_enabler || 'N/A',
              })
            )
          );
          setDataSourceType('rest');
          setInternalLoading(false);
          return;
        }
        if (data?.aiSolution?.fetch?.length) {
          setSolutionData(
            data.aiSolution.fetch.map(({ id, name, impactAssessment }: any, index: number) => ({
              id,
              serial: index + 1,
              opportunity_name: name,
              impact: impactAssessment?.general ?? 'N/A',
            }))
          );
          setDataSourceType('graphql');
          setInternalLoading(false);
        } else if (!qlLoading) {
          setSolutionData([]);
          setDataSourceType(null);
          setInternalLoading(false);
        }
      } catch (error) {
        console.error('Error loading solutions:', error);
        ShowToast({ message: 'Error loading solutions', type: ToastTypeEnum.Error });
        setSolutionData([]);
        setDataSourceType(null);
        setInternalLoading(false);
      }
    };
    loadSolutions();
  }, [fetchAISolution, data, qlLoading, refreshKey]);

  const columns = useMemo(
    () => [
      { id: 'serial', label: 'S. No.', minWidth: 80, align: 'center', sortable: true },
      {
        id: 'opportunity_name',
        label: 'Opportunity Name',
        minWidth: 200,
        sortable: true,
        clickable: true,
        link: true,
      },
      { id: 'impact', label: 'Impact', minWidth: 150, align: 'center', sortable: true },
      ...(dataSourceType === 'rest'
        ? [
            { id: 'roi', label: 'ROI', minWidth: 150, align: 'center', sortable: true },
            {
              id: 'complexity',
              label: 'Complexity',
              minWidth: 150,
              align: 'center',
              sortable: true,
            },
          ]
        : []),
      {
        id: 'actions',
        label: 'Actions',
        minWidth: 50,
        align: 'center',
        actions: { view: true, delete: true },
      },
    ],
    [dataSourceType]
  );

  const handleVaultChange = async (id: string, state: boolean) => {
    const url = `${API_BASE}/api/v1/e2e_taxonomy/new-vault/`;
    try {
      const response = await httpService<any>(
        state ? httpConstants.METHOD_TYPE.POST : httpConstants.METHOD_TYPE.DELETE,
        url,
        {
          organization_id: currentOrg,
          ai_solution_id: id,
        }
      );
      return !!response;
    } catch (error) {
      console.error('Vault update failed:', error);
      return false;
    }
  };

  const handleToggleVault = async () => {
    if (!selectedSolutionFromLibrary || !selectedSolutionFromLibrary.id) return;

    const newState = !selectedSolutionFromLibrary.vault;
    const success = await handleVaultChange(selectedSolutionFromLibrary.id, newState);

    if (success) {
      setSelectedSolutionFromLibrary({ ...selectedSolutionFromLibrary, vault: newState });

      // Update the solution data in the table
      setSolutionData((prevData) =>
        prevData.map((solution) =>
          solution.id === selectedSolutionFromLibrary.id
            ? { ...solution, vault: newState }
            : solution
        )
      );

      if (newState) {
        ShowToast({ message: 'Solution added to vault', type: ToastTypeEnum.Success });
      } else {
        ShowToast({ message: 'Solution removed from vault', type: ToastTypeEnum.Success });
      }
    } else {
      ShowToast({ message: 'Unable to process request', type: ToastTypeEnum.Error });
    }
    setShowVaultModal(false);
  };

  const handleCloseVaultModal = () => setShowVaultModal(false);

  const handleSave = async (data: any) => {
    // Update the solution data with the latest changes
    setAiSolutionData(data);

    // Update the solutionData state to reflect changes in the table immediately
    setSolutionData((prevData) =>
      prevData.map((solution) =>
        solution.id === selectedSolutionFromLibrary.id
          ? {
              ...solution,
              serial: solution.serial,
              roi: data.roi ? (
                <Box
                  sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}
                >
                  <img src={getSvgPath(data.roi)} alt={data.roi} style={{ width: 8, height: 8 }} />
                  <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>
                    {String(data.roi).charAt(0).toUpperCase() +
                      String(data.roi).slice(1).toLowerCase()}
                  </Typography>
                </Box>
              ) : (
                '-'
              ),
              complexity: data.complexity ? (
                <Box
                  sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}
                >
                  <img
                    src={getSvgPath(data.complexity)}
                    alt={data.complexity}
                    style={{ width: 8, height: 8 }}
                  />
                  <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>
                    {String(data.complexity).charAt(0).toUpperCase() +
                      String(data.complexity).slice(1).toLowerCase()}
                  </Typography>
                </Box>
              ) : (
                '-'
              ),
              impact: data.impact || solution.impact,
              solution_enabler: data.solution_enabler || solution.solution_enabler,
            }
          : solution
      )
    );

    // Make API call to persist changes to backend
    try {
      await patchSolution(data);

      // Refresh the detailed solution data to ensure ViewSolutionDetails shows updated data
      const updatedSolutionData = await fetchAISolutionByID(selectedSolutionFromLibrary.id);
      if (updatedSolutionData) {
        setAiSolutionData(updatedSolutionData);
      }

      ShowToast({ message: 'Solution updated successfully', type: ToastTypeEnum.Success });
    } catch (error) {
      console.error('Error updating solution:', error);
      ShowToast({ message: 'Error updating solution', type: ToastTypeEnum.Error });
    }
  };

  const handleUpdateSolution = (data: any) => {
    setAiSolutionData(data);
  };

  return (
    <Box
      sx={{
        minHeight: '0',
        p: 2,
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
        overflowY: 'hidden',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ flexGrow: 1, fontSize: '24px', fontWeight: 500 }}>
          AI Opportunities
        </Typography>
      </Box>

      {diagramData?.isRegenerating && diagramData?.regeneratingSubprocessId === selectedCard?.id ? (
        <Box
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '80vh' }}
        >
          <Loader message="AI Opportunities are being generated..." />
        </Box>
      ) : combinedLoading ? (
        <Box
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '80vh' }}
        >
          <Loader message="Loading AI Opportunities..." />
        </Box>
      ) : (
        <EnhancedDataTable
          columns={columns as any}
          rows={solutionData as any}
          hideScrollBar={true}
          height="80vh"
          onClick={(row: AiSolutionRow) =>
            setSelectedSolutionFromLibrary({
              id: row.id,
              name: row.opportunity_name,
              vault: row.vault,
              solution_enabler: row.solution_enabler || undefined,
            })
          }
          onView={(row: AiSolutionRow) =>
            setSelectedSolutionFromLibrary({
              id: row.id,
              name: row.opportunity_name,
              vault: row.vault,
              solution_enabler: row.solution_enabler || undefined,
            })
          }
          onDelete={(row: AiSolutionRow) => {
            setShowDeleteModal(true);
            setSolutionToDelete(row.id as string);
          }}
          showRowBorders
        />
      )}

      <Drawer
        open={!!selectedSolutionFromLibrary.id}
        onClose={() => setSelectedSolutionFromLibrary({ id: '', name: '' })}
        anchor="right"
        hideBackdrop
        ModalProps={{
          keepMounted: true,
          sx: {
            zIndex: 1400,
            '& .MuiDrawer-paper': {
              width: { xs: '100%', md: '70%' },
              pointerEvents: 'auto',
            },
          },
        }}
      >
        <Box>
          {dataSourceType === 'rest' && (
            <ViewSolutionDetails
              selectedSolutionId={selectedSolutionFromLibrary.id}
              selectedSolutionFromLibrary={selectedSolutionFromLibrary}
              enterpriseContextApplied={enterpriseContextApplied}
              setEnterpriseContextApplied={setEnterpriseContextApplied}
              aiSolutionData={aiSolutionData}
              setAiSolutionData={setAiSolutionData}
              diagramId={diagramId}
              handleToggleVault={handleToggleVault}
              handleUpdateSolution={handleUpdateSolution}
              handleSave={handleSave}
              processType={selectedCard?.processType || 'standard'}
            />
          )}
          {dataSourceType === 'graphql' && (
            <ViewSolutionDetails
              selectedSolutionId={selectedSolutionFromLibrary.id}
              selectedSolutionFromLibrary={selectedSolutionFromLibrary}
              enterpriseContextApplied={enterpriseContextApplied}
              setEnterpriseContextApplied={setEnterpriseContextApplied}
              aiSolutionData={aiSolutionData}
              setAiSolutionData={setAiSolutionData}
              handleToggleVault={handleToggleVault}
              handleUpdateSolution={handleUpdateSolution}
              handleSave={handleSave}
              processType={selectedCard?.processType || 'standard'}
            />
          )}
        </Box>
      </Drawer>

      <CustomModal
        isOpen={showVaultModal}
        onClose={handleCloseVaultModal}
        title={
          selectedSolutionFromLibrary?.vault
            ? 'Remove from Solution Vault'
            : 'Add to Solution Vault'
        }
        maxWidth="sm"
      >
        <Box
          sx={{
            bgcolor: '#F9FBFF',
            borderRadius: '12px',
            p: 3,
            display: 'flex',
            alignItems: 'end',
            justifyContent: 'stretch',
            flexDirection: 'column',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ fontSize: '16px', color: '#2c2c2c' }}>
              Are you sure you want to{' '}
              {selectedSolutionFromLibrary?.vault
                ? 'remove this AI Solution from'
                : 'add this AI Solution to'}{' '}
              your solution vault?
            </Typography>
          </Box>
          <Button
            variant="contained"
            onClick={handleToggleVault}
            sx={{
              textTransform: 'none',
              bgcolor: '#2054F4',
              borderRadius: '6px',
              px: 3,
              py: 1,
              mt: 2,
              '&:hover': { bgcolor: '#1A45C6' },
              fontWeight: 'bold',
            }}
          >
            {selectedSolutionFromLibrary?.vault ? 'Remove' : 'Add'}
          </Button>
        </Box>
      </CustomModal>

      <Modal
        open={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        sx={{ zIndex: 1600, display: 'flex', justifyContent: 'center', alignItems: 'center' }}
      >
        <Box
          sx={{
            width: '400px',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            height: 'auto',
            backgroundColor: 'white',
            p: 2,
            borderRadius: 1,
          }}
        >
          <Typography sx={{ textAlign: 'center' }}>
            Are you sure you want to delete this solution?
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Button variant="contained" onClick={() => setShowDeleteModal(false)}>
              Cancel
            </Button>
            <Button
              variant="contained"
              sx={{ backgroundColor: '#EF4444', '&:hover': { backgroundColor: '#EF4444' } }}
              onClick={() => deleteSolution(solutionToDelete)}
            >
              Delete
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default AiSolutions;
